import React from "react";
import { Minus, Plus, ShieldCheck, Truck, RotateCcw, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { VeyroHeader, VeyroFooter } from "@/components/VeyroLayout";
import VeyroCart from "@/components/VeyroCart";
import { VEYRO, SPECS, INCLUDED, SHIPPING_INFO, RETURNS_INFO, COLORS } from "@/lib/veyro";
import { useCart } from "@/context/CartContext";
import { createShopifyCheckout } from "@/lib/shopify";
import { trackPixelEvent } from "@/lib/metaPixel";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const GALLERY = [
  { src: VEYRO.images.black, alt: "Veyro backpack — black" },
  { src: VEYRO.images.navy, alt: "Veyro backpack — navy" },
  { src: VEYRO.images.overview, alt: "Veyro Vacuum Compression Travel Backpack overview", natural: true },
  { src: VEYRO.images.interiorDark, alt: "Multi-function vacuum backpack — interior storage layout", natural: true },
  { src: VEYRO.images.capacity, alt: "Expandable capacity — 28L to 60L", natural: true },
  { src: VEYRO.images.lock, alt: "Anti-theft TSA lock", natural: true },
];

const DETAIL_GRAPHICS = [
  { src: VEYRO.images.overview, alt: "Veyro™ Vacuum Compression Travel Backpack — product overview" },
  { src: VEYRO.images.interiorDark, alt: "Multi-function vacuum backpack — interior layout" },
  { src: VEYRO.images.howto, alt: "How to use the vacuum compression bag — 4 steps" },
  { src: VEYRO.images.zipper, alt: "Seal the vacuum zipper completely — wrong vs right" },
  { src: VEYRO.images.capacity, alt: "Expandable capacity — 28L daily, 36L expanded, 60L compressed" },
  { src: VEYRO.images.lock, alt: "Anti-theft TSA lock — setup and features" },
];

const ACCORDIONS = [
  { title: "Description", content: VEYRO.description + " Premium technical materials, ergonomic padded straps, organized compartments, and an anti-theft TSA lock — built for flights, weekends, and business travel." },
  { title: "Specifications", content: SPECS.map((s) => `${s.label}: ${s.value}`).join(" · ") + "" },
  { title: "Shipping", content: "Orders are processed within 1–3 business days. Standard shipping takes 3–7 business days; expedited options available at checkout. Tracking is emailed once your order ships." },
  { title: "Returns", content: "30-day return policy. Items must be unused, in original packaging, with proof of purchase. See our Returns & Refunds page for the full policy." },
];

export default function ProductPage() {
  const { addToCart, setQty, pricing, price, compareAt, shopifyProduct, color, setColor, variantId } = useCart();
  const [gallery, setGallery] = React.useState(0);
  const [buyQty, setBuyQty] = React.useState(1);
  const [buying, setBuying] = React.useState(false);
  const [ctaVisible, setCtaVisible] = React.useState(true);
  const ctaRef = React.useRef(null);
  const viewTracked = React.useRef(false);

  React.useEffect(() => {
    window.scrollTo(0, 0);
    if (viewTracked.current) return;
    viewTracked.current = true;
    trackPixelEvent("ViewContent", {
      content_name: VEYRO.name,
      content_type: "product",
      contents: [{ id: VEYRO.pixelId, quantity: 1 }],
      value: price,
      currency: "USD",
    });
  }, [price]);

  React.useEffect(() => {
    const el = ctaRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => setCtaVisible(entries[0].isIntersecting));
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const buyNow = async () => {
    setBuying(true);
    trackPixelEvent("InitiateCheckout", {
      content_name: VEYRO.name,
      content_type: "product",
      contents: [{ id: VEYRO.pixelId, quantity: buyQty }],
      num_items: buyQty,
      value: buyQty * price,
      currency: "USD",
    });
    try {
      if (!variantId) {
        toast.error("Checkout is being connected — please try again in a little while.");
        setBuying(false);
        return;
      }
      const url = await createShopifyCheckout([{ merchandiseId: variantId, quantity: buyQty }]);
      window.location.href = url;
    } catch (e) {
      console.error(e);
      toast.error("Checkout failed. Please try again.");
      setBuying(false);
    }
  };

  return (
    <div data-testid="product-page" className="min-h-screen bg-[#090A0E]">
      <VeyroHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-7">
          <div className="rounded-3xl overflow-hidden border border-[#282D3C] bg-[#0D0F15] flex items-center">
            <img
              data-testid="pdp-main-image"
              src={GALLERY[gallery].src}
              alt={GALLERY[gallery].alt}
              className={`w-full ${GALLERY[gallery].natural ? "h-auto" : "aspect-square object-cover"}`}
            />
          </div>
          <div className="mt-4 grid grid-cols-3 sm:grid-cols-6 gap-3">
            {GALLERY.map((g, i) => (
              <button key={i} data-testid={`pdp-thumb-${i}`} onClick={() => setGallery(i)} className={`rounded-xl overflow-hidden border-2 transition-colors ${gallery === i ? "border-[#00E5FF]" : "border-transparent hover:border-[#282D3C]"}`}>
                <img src={g.src} alt={g.alt} className="w-full aspect-square object-cover" />
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="mono-label text-xs text-[#00E5FF] font-bold">Veyro™ Compression Series</div>
          <h1 data-testid="pdp-title" className="mt-3 text-3xl sm:text-4xl font-black tracking-tight text-white leading-tight">
            {VEYRO.name}
          </h1>
          <div className="mt-3 mono-label text-[11px] text-slate-400 font-bold tracking-widest">
            Premium Travel Technology · Vacuum Compression System
          </div>
          <div className="mt-5 flex items-center gap-3 flex-wrap">
            <span data-testid="pdp-price" className="text-4xl font-black tracking-tight text-white">${price.toFixed(2)}</span>
            {compareAt && compareAt > price && (
              <>
                <span data-testid="pdp-compare-price" className="text-xl text-slate-500 line-through">${compareAt.toFixed(2)}</span>
                <span data-testid="pdp-sale-badge" className="mono-label text-[10px] font-bold bg-[#00E5FF]/10 border border-[#00E5FF]/30 text-[#00E5FF] rounded-full px-3 py-1">
                  Sale
                </span>
              </>
            )}
          </div>

          <div data-testid="pdp-color-selector" className="mt-7">
            <div className="text-sm text-slate-400">
              Color — <span data-testid="pdp-selected-color" className="text-white font-bold">{color}</span>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-3 max-w-sm">
              {COLORS.map((c) => (
                <button
                  key={c.name}
                  data-testid={`color-option-${c.name.toLowerCase()}`}
                  onClick={() => setColor(c.name)}
                  className={`rounded-xl overflow-hidden border-2 bg-[#0D0F15] transition-colors duration-200 ${
                    color === c.name
                      ? "border-[#00E5FF] shadow-[0_0_18px_rgba(0,229,255,0.35)]"
                      : "border-[#282D3C] hover:border-slate-500"
                  }`}
                >
                  <img src={c.image} alt={`Veyro backpack — ${c.name}`} className="w-full aspect-square object-cover" loading="lazy" />
                  <div className={`py-1.5 text-[11px] font-bold tracking-widest uppercase text-center ${color === c.name ? "text-[#00E5FF]" : "text-slate-300"}`}>
                    {c.name}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="mt-7 flex items-center gap-4">
            <div className="inline-flex items-center rounded-full border border-slate-700 bg-[#0D0F15]">
              <button data-testid="pdp-qty-dec" onClick={() => setBuyQty((q) => Math.max(1, q - 1))} className="p-3 text-slate-300"><Minus size={15} /></button>
              <span data-testid="pdp-qty-value" className="w-10 text-center font-bold text-white">{buyQty}</span>
              <button data-testid="pdp-qty-inc" onClick={() => setBuyQty((q) => Math.min(10, q + 1))} className="p-3 text-slate-300"><Plus size={15} /></button>
            </div>
            <span className="text-sm text-slate-500">Quantity</span>
          </div>

          <div className="mt-6 space-y-3">
            <button
              ref={ctaRef}
              data-testid="pdp-add-to-cart-btn"
              onClick={() => addToCart(buyQty)}
              className="w-full inline-flex items-center justify-center bg-[#00E5FF] hover:bg-[#33ECFF] text-[#090A0E] rounded-full px-8 py-4 text-lg font-bold transition-all duration-200 shadow-[0_0_30px_rgba(0,229,255,0.2)]"
            >
              Add to Cart — ${(buyQty * price).toFixed(2)}
            </button>
            <button
              data-testid="pdp-buy-now-btn"
              onClick={buyNow}
              disabled={buying}
              className="w-full inline-flex items-center justify-center bg-white hover:bg-slate-200 text-[#090A0E] rounded-full px-8 py-4 text-lg font-bold transition-colors duration-200 disabled:opacity-70"
            >
              {buying ? <span className="inline-flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Redirecting...</span> : "Buy Now"}
            </button>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-3">
            {[
              { Icon: RotateCcw, t: "30-Day Returns" },
              { Icon: Truck, t: "Fast Shipping" },
              { Icon: ShieldCheck, t: "Secure Checkout" },
            ].map(({ Icon, t }, i) => (
              <div key={i} className="rounded-xl bg-[#161922] border border-[#282D3C] p-4 text-center">
                <Icon size={18} strokeWidth={1.7} className="mx-auto text-[#00E5FF]" />
                <div className="mt-2 text-[11px] tracking-wide font-bold text-slate-200">{t}</div>
              </div>
            ))}
          </div>

          <div data-testid="pdp-included-box" className="mt-6 rounded-2xl bg-[#161922] border border-[#282D3C] p-5">
            <div className="mono-label text-[11px] text-[#00E5FF] font-bold">What's Included</div>
            <ul className="mt-3 space-y-2.5 text-sm text-slate-200">
              {INCLUDED.map((item, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <Check size={15} className="text-[#00E5FF] mt-0.5 shrink-0" /> {item}
                </li>
              ))}
            </ul>
          </div>

          <div data-testid="pdp-shipping-returns" className="mt-5 space-y-2 text-sm text-slate-400">
            <div className="flex items-start gap-2.5"><Truck size={16} className="text-[#00E5FF] mt-0.5 shrink-0" /> {SHIPPING_INFO}</div>
            <div className="flex items-start gap-2.5"><RotateCcw size={16} className="text-[#00E5FF] mt-0.5 shrink-0" /> {RETURNS_INFO}</div>
          </div>

          <ul className="mt-7 space-y-2.5 text-sm text-slate-300">
            {["Vacuum compression fits up to 60L of packed clothing", "Expands +4 inches when you need more room", "Anti-theft TSA combination lock built in", "Premium materials & ergonomic comfort straps"].map((b, i) => (
              <li key={i} className="flex gap-2.5 items-start">
                <span className="w-1.5 h-1.5 rounded-full bg-[#00E5FF] mt-2 shrink-0" />{b}
              </li>
            ))}
          </ul>

          <Accordion type="single" collapsible className="mt-8">
            {ACCORDIONS.map((a, i) => (
              <AccordionItem key={i} value={`acc-${i}`} className="border-[#282D3C]">
                <AccordionTrigger data-testid={`pdp-accordion-${i}`} className="text-slate-200 hover:text-white hover:no-underline">{a.title}</AccordionTrigger>
                <AccordionContent className="text-slate-400 leading-relaxed">{a.content}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </main>

      <section data-testid="pdp-graphics-section" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 pb-20 lg:pb-28">
        <div className="mono-label text-xs text-[#00E5FF] font-bold">Product Details</div>
        <h2 className="mt-4 text-2xl sm:text-3xl font-bold tracking-tight text-white">Every detail, engineered.</h2>
        <div className="mt-10 space-y-8">
          {DETAIL_GRAPHICS.map((g, i) =>
            g.framed ? (
              <div key={i} data-testid={`pdp-graphic-${i}`} className="rounded-3xl bg-[#0D0F15] border border-[#282D3C] overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3 border-b border-[#282D3C] bg-[#0A0C11]">
                  <span className="text-sm font-black tracking-tighter text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    VEYRO<span className="text-[#00E5FF]">™</span>
                  </span>
                  <span className="mono-label text-[10px] text-[#00E5FF]/80 font-bold">{g.label}</span>
                </div>
                <div className="p-4 sm:p-5">
                  <img src={g.src} alt={g.alt} className="w-full h-auto rounded-2xl border border-[#1C202B]" loading="lazy" />
                </div>
              </div>
            ) : (
              <div key={i} data-testid={`pdp-graphic-${i}`} className="rounded-3xl overflow-hidden border border-[#282D3C] bg-[#0D0F15]">
                <img src={g.src} alt={g.alt} className="w-full h-auto" loading="lazy" />
              </div>
            )
          )}
        </div>
      </section>

      <VeyroFooter />
      <VeyroCart />

      <div
        data-testid="sticky-add-to-cart-bar"
        className={`md:hidden fixed bottom-0 left-0 right-0 z-[80] bg-[#0D0F15]/95 backdrop-blur-lg border-t border-[#282D3C] px-4 py-3 flex items-center gap-3 transition-transform duration-300 ${!ctaVisible ? "translate-y-0" : "translate-y-full"}`}
        style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
      >
        <img src={VEYRO.images.black} alt={VEYRO.shortName} className="w-11 h-11 rounded-lg object-cover border border-[#282D3C] shrink-0" />
        <div className="min-w-0">
          <div className="text-sm font-bold text-white truncate">{VEYRO.shortName}</div>
          <div className="text-xs text-slate-400">
            <span className="font-black text-[#00E5FF]">${price.toFixed(2)}</span>
            {compareAt && <span className="line-through ml-1.5">${compareAt.toFixed(2)}</span>}
          </div>
        </div>
        <button data-testid="sticky-add-to-cart-btn" onClick={() => addToCart(buyQty)} className="ml-auto shrink-0 inline-flex items-center justify-center bg-[#00E5FF] text-[#090A0E] rounded-full px-6 py-3 text-sm font-bold">
          Add to Cart
        </button>
      </div>
    </div>
  );
}
